import {useEffect, useState} from 'react';

import {CurrencyPicker} from '@/components';
import type {AfriexRate} from '@/types';
import {AfriexCurrencyCode} from '@/types';
import {formatRate} from '@/utils/formatRate';
import {applyDiscountToRates} from '@/utils/rates/applyDiscountToRates';
import {convertRatesListToMap} from '@/utils/rates/convertRatesListToMap';

type Props = {
  rates: AfriexRate[];
  discount: number;
  targetCurrency?: AfriexCurrencyCode;
};
const RateWidget = ({rates, discount, targetCurrency}: Props) => {
  const [fromCurrencyCode, setFromCurrencyCode] = useState(
    AfriexCurrencyCode.USD,
  );
  const [toCurrencyCode, setToCurrencyCode] = useState(AfriexCurrencyCode.NGN);
  useEffect(() => {
    if (!rates) {
      return;
    }
    if (targetCurrency) {
      setToCurrencyCode(targetCurrency);
    }
  }, [fromCurrencyCode, toCurrencyCode, discount, targetCurrency, rates]);
  const ratesMap = convertRatesListToMap(rates);
  const ratesMapWithDiscount = convertRatesListToMap(
    applyDiscountToRates(rates, discount),
  );
  // `RatesValueMap` leaves are wire strings (`AfriexRate.value: string`), so
  // these lookups yield `string | 0`. `formatRate` takes a number and already
  // stringifies internally, so `Number(...)` is what the removed `any` was
  // silently doing.
  const rate = ratesMapWithDiscount?.[fromCurrencyCode]?.[toCurrencyCode] || 0;
  const baseRate = ratesMap?.[AfriexCurrencyCode.USD]?.[toCurrencyCode] || 0;
  return (
    <div className="bg-gray-100 dark:bg-gray-900 p-4 flex flex-col items-baseline">
      <div className="flex gap-3">
        <CurrencyPicker
          label="From"
          value={fromCurrencyCode}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setFromCurrencyCode(e.target.value as AfriexCurrencyCode)
          }
        />
        <CurrencyPicker
          label="To"
          value={toCurrencyCode}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setToCurrencyCode(e.target.value as AfriexCurrencyCode)
          }
        />
        <div className="text-sm mt-1">
          <label className="block text-gray-600 dark:text-gray-200 text-sm font-medium mb-2">
            Base Rate
          </label>
          <span className="text-sm text-gray-700 font-medium dark:text-gray-100">
            {formatRate(Number(baseRate))}
          </span>
        </div>
      </div>

      <p className="mt-2 text-xs text-blue-600 font-medium">
        Rate with Discount:{' '}
        <span className="text-sm text-gray-700 dark:text-gray-100 font-medium">
          {formatRate(Number(rate), 9)}
        </span>
      </p>
    </div>
  );
};

export default RateWidget;
